import React, { useState } from 'react';
import { TextField, Button, CircularProgress, Backdrop } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FiCalendar, FiClock, FiUser, FiMail, FiPhone, FiBriefcase, FiFileText } from 'react-icons/fi';
import Navbar from "./Navbar";
import Footer from "./Footer";
import './BookDemo.css';

const BookDemo = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required('Full Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phoneNumber: Yup.string().required('Phone number is required'),
    company: Yup.string().required('Company is required'),
    note: Yup.string(),
    selectedDate: Yup.date().nullable().required('Date is required'),
    selectedTime: Yup.date().nullable().required('Time is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const bookedTime = values.selectedDate && values.selectedTime
        ? new Date(values.selectedDate).setHours(values.selectedTime.getHours(), values.selectedTime.getMinutes())
        : '';
      
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/book-demo`, {
        full_name: values.fullName,
        email: values.email,
        phone_number: values.phoneNumber,
        company: values.company,
        note: values.note,
        booked_time: new Date(bookedTime).toISOString(),
      });
      
      alert(response.data.message);
      navigate("/");
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="book-demo-page">
      <Navbar />
      
      <div className="book-demo-wrapper">
        <div className="book-demo-container">
          <div className="book-demo-header">
            <h1 className="book-demo-title">Schedule Your Demo</h1>
            <p className="book-demo-subtitle">
              Discover how Lume Solutions can transform your lending operations. 
              Book a personalized demo with our team.
            </p>
          </div>

          <Formik
            initialValues={{
              fullName: '',
              email: '',
              phoneNumber: '',
              company: '',
              note: '',
              selectedDate: null,
              selectedTime: null,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, isSubmitting }) => (
              <>
                <Backdrop
                  sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                  open={isSubmitting}
                >
                  <CircularProgress color="inherit" />
                </Backdrop>

                <Form className="book-demo-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <FiUser className="label-icon" />
                        Full Name
                      </label>
                      <Field
                        as={TextField}
                        name="fullName"
                        placeholder="John Doe"
                        fullWidth
                        variant="outlined"
                        className="form-input"
                      />
                      <ErrorMessage name="fullName" component="div" className="error-message" />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FiMail className="label-icon" />
                        Email Address
                      </label>
                      <Field
                        as={TextField}
                        name="email"
                        type="email"
                        placeholder="john@company.com"
                        fullWidth
                        variant="outlined"
                        className="form-input"
                      />
                      <ErrorMessage name="email" component="div" className="error-message" />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FiPhone className="label-icon" />
                        Phone Number
                      </label>
                      <Field
                        as={TextField}
                        name="phoneNumber"
                        placeholder="+254 712 345 678"
                        fullWidth
                        variant="outlined"
                        className="form-input"
                      />
                      <ErrorMessage name="phoneNumber" component="div" className="error-message" />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FiBriefcase className="label-icon" />
                        Company Name
                      </label>
                      <Field
                        as={TextField}
                        name="company"
                        placeholder="Your Company Ltd"
                        fullWidth
                        variant="outlined"
                        className="form-input"
                      />
                      <ErrorMessage name="company" component="div" className="error-message" />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FiCalendar className="label-icon" />
                        Preferred Date
                      </label>
                      <DatePicker
                        selected={selectedDate}
                        onChange={(date) => {
                          setSelectedDate(date);
                          setFieldValue('selectedDate', date);
                        }}
                        dateFormat="MMMM d, yyyy"
                        minDate={new Date()}
                        filterDate={(date) => date.getDay() !== 0 && date.getDay() !== 6}
                        placeholderText="Select a date"
                        className="custom-datepicker"
                      />
                      <ErrorMessage name="selectedDate" component="div" className="error-message" />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FiClock className="label-icon" />
                        Preferred Time
                      </label>
                      <DatePicker
                        selected={selectedTime}
                        onChange={(time) => {
                          setSelectedTime(time);
                          setFieldValue('selectedTime', time);
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={30}
                        timeCaption="Time"
                        dateFormat="h:mm aa"
                        minTime={new Date().setHours(9, 0)}
                        maxTime={new Date().setHours(17, 0)}
                        placeholderText="Select a time"
                        className="custom-timepicker"
                      />
                      <ErrorMessage name="selectedTime" component="div" className="error-message" />
                    </div>

                    <div className="form-group form-group-full">
                      <label className="form-label">
                        <FiFileText className="label-icon" />
                        Additional Notes (Optional)
                      </label>
                      <Field
                        as={TextField}
                        name="note"
                        placeholder="Tell us about your specific needs..."
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    className="submit-button"
                  >
                    {isSubmitting ? 'Booking...' : 'Book Demo'}
                  </Button>
                </Form>
              </>
            )}
          </Formik>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookDemo;